import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../Shell';
import { useDrawer } from '../drawer/context';
import { DueBadge, EmptyProgram, Owner, Pill, daysUntil, fmtDate } from '../primitives';
import type { PillKind } from '../primitives';
import type { ProgramResponse, Severity } from '../../lib/types';

interface ViewProps {
  program: ProgramResponse | null;
  loading: boolean;
}

const SEED_HINT =
  'CyberWISE ships with an empty program on purpose. The sample rows in the design handoff are ' +
  'shaped like real payloads, not real records — seeding them would put invented compliance ' +
  'status in front of the people who rely on it. Load the real program to populate this view.';

function Card({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-h">
        <h3>{title}</h3>
        {hint && <span className="hint">{hint}</span>}
      </div>
      <div className="card-b">{children}</div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="content">
      {Array.from({ length: 6 }, (_, i) => (
        <div className="cw-skel" key={i} style={{ width: `${90 - i * 6}%`, marginBottom: 12 }} />
      ))}
    </div>
  );
}

/** Tables scroll horizontally inside their card, never the page body. */
const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ overflowX: 'auto' }}>{children}</div>
);

const severityPill: Record<Severity, PillKind> = {
  critical: 'bad',
  high: 'warn',
  medium: 'warn',
  low: 'neutral',
};

// ── Incidents ───────────────────────────────────────────────────────────────

const incidentStatusPill = (s: string): PillKind =>
  s === 'Closed' ? 'ok' : s === 'Triage' ? 'warn' : 'info';

export function Incidents({ program, loading }: ViewProps) {
  const openDrawer = useDrawer();
  if (loading || !program) return <Skeleton />;
  const rows = program.incidents;

  return (
    <>
      <PageHeader title="Incidents" subtitle="Investigations from detection through closure" />
      <div className="content">
        <Card title={`Incidents — ${rows.length}`}>
          {rows.length === 0 ? (
            <EmptyProgram what="incidents" why={SEED_HINT} />
          ) : (
            <Scroll>
              <table className="t">
                <thead>
                  <tr>
                    <th>Incident</th>
                    <th>Severity</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Analyst</th>
                    <th>Opened</th>
                    <th>Closed</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((i) => (
                    <tr key={i.id} onClick={() => openDrawer({ kind: 'incident', id: i.id })}>
                      <td>
                        <b>{i.ref}</b>
                        <span className="sub2">{i.title}</span>
                      </td>
                      <td>
                        <Pill kind={severityPill[i.severity]}>{i.severity}</Pill>
                      </td>
                      <td>{i.source ?? <span className="na">—</span>}</td>
                      <td>
                        <Pill kind={incidentStatusPill(i.status)}>{i.status}</Pill>
                      </td>
                      <td>
                        <Owner refKey={i.analyst_ref} analysts={program.analysts} />
                      </td>
                      <td>{fmtDate(i.opened)}</td>
                      {/* An open incident shows an em-dash, never today's date */}
                      <td>{i.closed ? fmtDate(i.closed) : <span className="na">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Scroll>
          )}
        </Card>
      </div>
    </>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

const controlStatusPill = (s: string): PillKind =>
  s === 'met' ? 'ok' : s === 'gap' ? 'bad' : s === 'in_progress' ? 'info' : 'neutral';

const controlStatusLabel = (s: string): string =>
  s === 'met' ? 'Met' : s === 'gap' ? 'Gap' : s === 'in_progress' ? 'In progress' : 'Not assessed';

export function Controls({ program, loading }: ViewProps) {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const openDrawer = useDrawer();
  const framework = params.get('framework') ?? 'all';

  const rows = useMemo(() => {
    if (!program) return [];
    const term = q.trim().toLowerCase();
    return program.controls.filter((c) => {
      if (framework !== 'all' && c.framework !== framework) return false;
      if (!term) return true;
      return [c.ref, c.name, c.owner_ref, c.scope, c.framework]
        .some((v) => (v ?? '').toLowerCase().includes(term));
    });
  }, [program, framework, q]);

  if (loading || !program) return <Skeleton />;

  const setFramework = (f: string) => {
    const next = new URLSearchParams(params);
    if (f === 'all') next.delete('framework');
    else next.set('framework', f);
    setParams(next, { replace: true });
  };

  const chips = [
    { key: 'all', label: 'All frameworks', count: program.controls.length },
    ...program.frameworks.map((f) => ({ key: f.framework, label: f.framework, count: f.total })),
  ];

  return (
    <>
      <PageHeader
        title="Controls"
        subtitle="The compliance core — a control is the unit of work"
      />
      <div className="content">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          {chips.map((c) => (
            <button
              key={c.key}
              className={`chip${framework === c.key ? ' on' : ''}`}
              onClick={() => setFramework(c.key)}
              type="button"
            >
              {c.label} <span className="k">{c.count}</span>
            </button>
          ))}
          <input
            className="search"
            style={{ marginLeft: 'auto' }}
            placeholder="Search controls, owners, scope"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search controls"
          />
        </div>

        <Card title={`Controls — ${rows.length}`}>
          {program.controls.length === 0 ? (
            <EmptyProgram what="controls" why={SEED_HINT} />
          ) : rows.length === 0 ? (
            <EmptyProgram what="matching controls" why="Clear the search or widen the framework filter." />
          ) : (
            <Scroll>
              <table className="t">
                <thead>
                  <tr>
                    <th>Control</th>
                    <th>Framework</th>
                    <th>Status</th>
                    <th>Owner</th>
                    <th>Evidence</th>
                    <th>Next review</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id} onClick={() => openDrawer({ kind: 'control', id: c.id })}>
                      <td>
                        <b>{c.ref}</b>
                        <span className="sub2">{c.name}</span>
                      </td>
                      <td>
                        <Pill kind="neutral">{c.framework}</Pill>
                      </td>
                      <td>
                        <Pill kind={controlStatusPill(c.status)}>{controlStatusLabel(c.status)}</Pill>
                        {c.finding && (
                          <span className="sub2" style={{ maxWidth: 220, whiteSpace: 'normal' }}>
                            {c.finding}
                          </span>
                        )}
                      </td>
                      <td>
                        <Owner refKey={c.owner_ref} analysts={program.analysts} />
                      </td>
                      <td>
                        {c.evidence_artifact ? (
                          <>
                            {c.evidence_artifact}
                            <span className="sub2">
                              {c.evidence_collected_at
                                ? `${daysUntil(c.evidence_collected_at) !== null ? Math.abs(daysUntil(c.evidence_collected_at)!) : '?'} d old`
                                : 'collecting now'}
                            </span>
                          </>
                        ) : (
                          <span className="na">—</span>
                        )}
                      </td>
                      <td>
                        <DueBadge date={c.next_review} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Scroll>
          )}
        </Card>
      </div>
    </>
  );
}

// ── Deadlines ───────────────────────────────────────────────────────────────

export function Deadlines({ program, loading }: ViewProps) {
  const openDrawer = useDrawer();
  if (loading || !program) return <Skeleton />;
  const rows = program.deadlines;

  const overdue = rows.filter((d) => (daysUntil(d.due) ?? 0) < 0 && d.status !== 'Complete');
  const due30 = rows.filter((d) => {
    const n = daysUntil(d.due);
    return n !== null && n >= 0 && n <= 30;
  });
  const atRisk = rows.filter((d) => d.status === 'At risk');
  const onTrack = rows.filter((d) => d.status === 'On track');

  // Month-grouped timeline, matching the prototype's calendar card.
  const months = useMemo(() => {
    const map = new Map<string, typeof rows>();
    for (const d of [...rows].sort((a, b) => a.due.localeCompare(b.due))) {
      const key = new Date(d.due).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      map.set(key, [...(map.get(key) ?? []), d]);
    }
    return [...map.entries()];
  }, [rows]);

  return (
    <>
      <PageHeader title="Deadlines" subtitle="Scans, audits, trainings, attestations, remediations" />
      <div className="content">
        <div className="stats c4">
          <div className="stat r">
            <span className="l">Overdue</span>
            <span className="v2">{overdue.length}</span>
            <span className="h">past their due date</span>
          </div>
          <div className="stat w">
            <span className="l">Due in 30 days</span>
            <span className="v2">{due30.length}</span>
            <span className="h">next month of work</span>
          </div>
          <div className="stat">
            <span className="l">At risk</span>
            <span className="v2">{atRisk.length}</span>
            <span className="h">flagged by an owner</span>
          </div>
          <div className="stat g">
            <span className="l">On track</span>
            <span className="v2">{onTrack.length}</span>
            <span className="h">no action needed</span>
          </div>
        </div>

        <div className="mt">
          <Card title="Program calendar">
            {rows.length === 0 ? (
              <EmptyProgram what="calendar items" why={SEED_HINT} />
            ) : (
              months.map(([month, items]) => (
                <div className="ti" key={month}>
                  <div className="d">
                    <b>{month.split(' ')[0]}</b>
                    <span>{month.split(' ')[1]}</span>
                    <span>{items.length} items</span>
                  </div>
                  <div className="body">
                    {items.map((d) => (
                      <div className="t1" key={d.id} onClick={() => openDrawer({ kind: 'deadline', id: d.id })} style={{ cursor: 'pointer' }}>
                        <b>{d.title}</b>
                        <span className="t2">
                          {[d.type, d.framework, d.owner_ref && program.analysts[d.owner_ref]]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                        <DueBadge date={d.due} />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

// ── Requests ────────────────────────────────────────────────────────────────

const requestStatusPill = (s: string): PillKind =>
  s === 'Overdue' ? 'bad' : s === 'New' ? 'info' : s === 'Closed' || s.startsWith('Approved') ? 'ok' : 'warn';

const requestKindPill = (k: string): PillKind =>
  k === 'Exception' ? 'violet' : k === 'Vendor review' ? 'teal' : 'neutral';

export function Requests({ program, loading }: ViewProps) {
  const openDrawer = useDrawer();
  if (loading || !program) return <Skeleton />;
  const rows = program.requests;
  const open = rows.filter((r) => !['Closed', 'Approved', 'Denied'].includes(r.status));
  const late = open.filter((r) => (daysUntil(r.sla_due) ?? 1) < 0);

  return (
    <>
      <PageHeader title="Requests" subtitle="Questions, exceptions and access reviews from staff" />
      <div className="content">
        <div className="stats c4">
          <div className="stat">
            <span className="l">Open</span>
            <span className="v2">{open.length}</span>
            <span className="h">questions, exceptions, reviews</span>
          </div>
          <div className="stat r">
            <span className="l">Past SLA</span>
            <span className="v2">{late.length}</span>
            <span className="h">answer today</span>
          </div>
          <div className="stat w">
            <span className="l">Exceptions active</span>
            <span className="v2">{rows.filter((r) => r.kind === 'Exception').length}</span>
            <span className="h">each one expires</span>
          </div>
          <div className="stat g">
            <span className="l">Median response</span>
            {/* Needs closed-request history to compute. Unknown, not zero. */}
            <span className="v2">
              <span className="cw-unread">—</span>
            </span>
            <span className="h">no closed requests yet</span>
          </div>
        </div>

        <div className="mt">
          <Card title={`Intake queue — ${rows.length}`}>
            {rows.length === 0 ? (
              <EmptyProgram
                what="requests"
                why="Intake channels (Teams message extension, the WISE launcher, email-to-queue) are not wired yet, so nothing can arrive."
              />
            ) : (
              <Scroll>
                <table className="t">
                  <thead>
                    <tr>
                      <th>Request</th>
                      <th>Type</th>
                      <th>Requester</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>SLA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const closed = ['Closed', 'Approved', 'Denied'].includes(r.status);
                      return (
                        <tr key={r.id} onClick={() => openDrawer({ kind: 'request', id: r.id })}>
                          <td>
                            <b>{r.ref}</b>
                            <span className="sub2">{r.title}</span>
                          </td>
                          <td>
                            <Pill kind={requestKindPill(r.kind)}>{r.kind}</Pill>
                          </td>
                          <td>
                            {r.requester ?? <span className="na">—</span>}
                            {r.department && <span className="sub2">{r.department}</span>}
                          </td>
                          <td>{r.priority}</td>
                          <td>
                            <Pill kind={requestStatusPill(r.status)}>{r.status}</Pill>
                          </td>
                          <td>
                            <DueBadge
                              date={r.sla_due}
                              closedLabel={
                                closed && r.sla_due ? `met ${fmtDate(r.sla_due)}` : undefined
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Scroll>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

// ── Vendor risk ─────────────────────────────────────────────────────────────

const vendorStatusPill = (s: string): PillKind =>
  s === 'Approved' ? 'ok' : s === 'In review' ? 'info' : 'bad';

export function Vendors({ program, loading }: ViewProps) {
  const openDrawer = useDrawer();
  if (loading || !program) return <Skeleton />;
  const rows = program.vendors;
  const live = rows.filter((v) => v.review_status === 'Live without review');

  return (
    <>
      <PageHeader
        title="Vendor Risk"
        subtitle="Third-party reviews that gate contract signature"
      />
      <div className="content">
        <div className="stats c4">
          <div className="stat r">
            <span className="l">Live without review</span>
            <span className="v2">{live.length}</span>
            <span className="h">SOC 2 CC9.2 exposure</span>
          </div>
          <div className="stat">
            <span className="l">Reviews in flight</span>
            <span className="v2">{rows.filter((v) => v.review_status === 'In review').length}</span>
            <span className="h">questionnaires out</span>
          </div>
          <div className="stat w">
            <span className="l">High inherent risk</span>
            <span className="v2">{rows.filter((v) => v.inherent_risk === 'High').length}</span>
            <span className="h">before controls</span>
          </div>
          <div className="stat g">
            <span className="l">Approved</span>
            <span className="v2">{rows.filter((v) => v.review_status === 'Approved').length}</span>
            <span className="h">disposition recorded</span>
          </div>
        </div>

        <div className="mt">
          <Card
            title={`Vendors — ${rows.length}`}
            hint="Feeds ContractWISE — no signature without a disposition"
          >
            {rows.length === 0 ? (
              <EmptyProgram what="vendors" why={SEED_HINT} />
            ) : (
              <Scroll>
                <table className="t">
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Category</th>
                      <th>Inherent risk</th>
                      <th>Data handled</th>
                      <th>Attestation</th>
                      <th>Review</th>
                      <th>Next action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((v) => (
                      <tr key={v.id} onClick={() => openDrawer({ kind: 'vendor', id: v.id })}>
                        <td>
                          <b>{v.name}</b>
                          <span className="sub2">
                            {v.owner_ref ? (program.analysts[v.owner_ref] ?? v.owner_ref) : 'Unassigned'}
                          </span>
                        </td>
                        <td>{v.category ?? <span className="na">—</span>}</td>
                        <td>
                          <Pill kind={v.inherent_risk === 'High' ? 'bad' : 'neutral'}>
                            {v.inherent_risk ?? 'unrated'}
                          </Pill>
                        </td>
                        <td>{v.data_handled ?? <span className="na">—</span>}</td>
                        <td>{v.attestation ?? <span className="na">—</span>}</td>
                        <td>
                          <Pill kind={vendorStatusPill(v.review_status)}>{v.review_status}</Pill>
                        </td>
                        <td>
                          <DueBadge date={v.next_action_due} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Scroll>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

// ── Awareness ───────────────────────────────────────────────────────────────

export function Awareness({ program, loading }: ViewProps) {
  if (loading || !program) return <Skeleton />;
  const rows = program.awareness;

  const pctOf = (p: (typeof rows)[number]): number | null =>
    p.audience_count && p.completed_count !== null
      ? Math.round((p.completed_count / p.audience_count) * 100)
      : null;

  return (
    <>
      <PageHeader
        title="Awareness"
        subtitle="Training and phishing simulation — PCI 12.6.3 evidence"
      />
      <div className="content">
        <Card title={`Programs — ${rows.length}`}>
          {rows.length === 0 ? (
            <EmptyProgram what="awareness programs" why={SEED_HINT} />
          ) : (
            <Scroll>
              <table className="t">
                <thead>
                  <tr>
                    <th>Program</th>
                    <th>Type</th>
                    <th>Audience</th>
                    <th>Completion</th>
                    <th>Fail rate</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => {
                    const pct = pctOf(p);
                    const tone = pct === null ? '' : pct > 95 ? 'g' : pct > 85 ? 'w' : 'r';
                    return (
                      <tr key={p.id}>
                        <td>
                          <b>{p.name}</b>
                        </td>
                        <td>
                          <Pill kind={p.type === 'Simulation' ? 'violet' : 'neutral'}>{p.type}</Pill>
                        </td>
                        <td>{p.audience_count ?? <span className="na">—</span>}</td>
                        <td>
                          {pct === null ? (
                            <span style={{ color: '#a8b5c6' }}>not started</span>
                          ) : (
                            <span className="pwrap">
                              <span className="bar">
                                <i className={tone} style={{ width: `${pct}%` }} />
                              </span>
                              <span>{pct}%</span>
                            </span>
                          )}
                        </td>
                        <td>{p.fail_rate === null ? <span className="na">—</span> : `${p.fail_rate}%`}</td>
                        <td>
                          <Pill kind={p.status === 'Complete' ? 'ok' : 'info'}>{p.status}</Pill>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Scroll>
          )}
        </Card>
      </div>
    </>
  );
}
